import { SprocketIcon } from 'foundations-assets/icons/components/SprocketIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Image } from 'foundations-components/transitional/components/Image';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import unescapedText from 'I18n/utils/unescapedText';
import { ACTION_TYPE_ICON_MAP, ICON_HOLDER_OUTER_SIZE, ICON_HOLDER_SIZE, ICON_URL_IMAGE_MARGIN_BLOCK_START, ICON_URL_IMAGE_SIZE, MAX_VISIBLE_ICONS } from './constants/icon-group';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// This is to keep backwards compatibility with the old action type strings.
// It should be removed once we fully migrate to the new icon system.
export function workflowActionTypesToIcons(actionTypes = []) {
  return actionTypes.map(actionType => ({
    actionType
  }));
}
const DEFAULT_ENTRY = {
  icon: SprocketIcon,
  iconColor: 'fill-accent-neutral-default',
  backgroundColor: 'fill-accent-neutral-subtle-alt'
};
function getActionTypeEntry(actionType) {
  var _ACTION_TYPE_ICON_MAP;
  return (_ACTION_TYPE_ICON_MAP = ACTION_TYPE_ICON_MAP[actionType]) !== null && _ACTION_TYPE_ICON_MAP !== void 0 ? _ACTION_TYPE_ICON_MAP : DEFAULT_ENTRY;
}
function getActionTypeLabel(actionType) {
  // If the action type is not in the ACTION_TYPE_ICON_MAP,
  // return the localized string returned from the BE.
  if (!(actionType in ACTION_TYPE_ICON_MAP)) {
    return actionType;
  }
  return unescapedText(`marketplace-ui-common.workflowTemplates.actionTypes.${actionType}`);
}
function resolveIcons({
  actionIcons,
  builtInWorkflowActionTypes
}) {
  // Gated -- new path: WorkflowTemplateIcon[] from productFeatures (supports iconUrl + action types).
  if (actionIcons != null) {
    return actionIcons;
  }

  // Ungated -- legacy path: action type strings only
  if (builtInWorkflowActionTypes !== null && builtInWorkflowActionTypes !== void 0 && builtInWorkflowActionTypes.length) {
    return workflowActionTypesToIcons(builtInWorkflowActionTypes);
  }
  return [];
}

/** ACTION_TYPE icons dedupe by Trellis category icon; APP icons dedupe by iconUrl. */
function dedupeIcons(icons) {
  const seenActionIcons = new Set();
  const seenImageUrls = new Set();
  const deduped = [];
  for (const icon of icons) {
    if (icon.iconUrl) {
      if (!seenImageUrls.has(icon.iconUrl)) {
        seenImageUrls.add(icon.iconUrl);
        deduped.push(icon);
      }
      continue;
    }
    const {
      icon: iconComponent
    } = getActionTypeEntry(icon.actionType);
    if (!seenActionIcons.has(iconComponent)) {
      seenActionIcons.add(iconComponent);
      deduped.push(icon);
    }
  }
  return deduped;
}
function getIconLabel(icon) {
  if (icon.iconUrl) {
    var _icon$label;
    return (_icon$label = icon.label) !== null && _icon$label !== void 0 ? _icon$label : icon.actionType;
  }
  return getActionTypeLabel(icon.actionType);
}
function getIconKey(icon) {
  if (icon.iconUrl) {
    return icon.iconUrl;
  }
  return icon.actionType;
}
export function WorkflowTemplateIconGroup({
  actionIcons,
  builtInWorkflowActionTypes,
  maxIcons = MAX_VISIBLE_ICONS
}) {
  // Resolve icons from the new and legacy paths based on gate
  const resolvedIcons = resolveIcons({
    actionIcons,
    builtInWorkflowActionTypes
  });
  if (!resolvedIcons.length) {
    return null;
  }
  const deduped = dedupeIcons(resolvedIcons);
  const visibleIcons = deduped.slice(0, maxIcons);
  const remainingCount = deduped.length - visibleIcons.length;
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    gap: "200",
    "aria-label": unescapedText('marketplace-ui-common.workflowTemplates.iconGroupLabel'),
    role: "group",
    children: [/*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      gap: "100",
      children: visibleIcons.map(icon => {
        const label = getIconLabel(icon);
        if (icon.iconUrl) {
          return /*#__PURE__*/_jsx(Tooltip, {
            title: label,
            children: /*#__PURE__*/_jsx(Flex, {
              alignItems: "center",
              blockSize: ICON_HOLDER_OUTER_SIZE,
              inlineSize: ICON_HOLDER_OUTER_SIZE,
              justifyContent: "center",
              marginBlockStart: ICON_URL_IMAGE_MARGIN_BLOCK_START,
              children: /*#__PURE__*/_jsx(Image, {
                alt: label,
                blockSize: ICON_URL_IMAGE_SIZE,
                display: "block",
                inlineSize: ICON_URL_IMAGE_SIZE,
                objectFit: "contain",
                src: icon.iconUrl
              })
            })
          }, getIconKey(icon));
        }
        const {
          icon: IconComponent,
          iconColor,
          backgroundColor
        } = getActionTypeEntry(icon.actionType);
        return /*#__PURE__*/_jsx(Tooltip, {
          title: label,
          children: /*#__PURE__*/_jsx(IconHolder, {
            "aria-label": label,
            backgroundColor: backgroundColor,
            borderRadius: "200",
            color: iconColor,
            icon: IconComponent,
            size: ICON_HOLDER_SIZE
          })
        }, getIconKey(icon));
      })
    }), remainingCount > 0 && /*#__PURE__*/_jsxs(Text, {
      typography: "body-100",
      color: "text-core-subtle",
      "aria-label": unescapedText('marketplace-ui-common.workflowTemplates.moreActionTypes', {
        count: remainingCount
      }),
      children: ["+", remainingCount]
    })]
  });
}